import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Key, Check, FileCode, RotateCcw, Terminal, Lock, Unlock, ChevronRight, ShieldAlert } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { PUZZLE_1_WORD_SCRAMBLE } from '@/config/missionContent';

const WORDS = PUZZLE_1_WORD_SCRAMBLE.words.map(w => w.toUpperCase());
const WORD_HINTS = PUZZLE_1_WORD_SCRAMBLE.wordHints;
const WORD_FILES = PUZZLE_1_WORD_SCRAMBLE.wordFiles;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// ─── Background: Enhanced Data Rain ───
function DataRain() {
  const columns = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      left: `${(i / 18) * 100}%`,
      duration: 5 + Math.random() * 8,
      delay: Math.random() * 4,
      glow: i < 4,
      chars: Array.from({ length: 30 }, () => String.fromCharCode(65 + Math.floor(Math.random() * 26))),
    })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {columns.map((col, i) => (
        <motion.div
          key={i}
          initial={{ y: '-100%' }}
          animate={{ y: '100%' }}
          transition={{ duration: col.duration, delay: col.delay, repeat: Infinity, ease: 'linear' }}
          className={`absolute font-terminal text-xs leading-tight whitespace-nowrap ${col.glow ? 'text-green-400/[0.07]' : 'text-green-500/[0.035]'}`}
          style={{ left: col.left, filter: col.glow ? 'drop-shadow(0 0 2px rgba(0,255,65,0.15))' : undefined }}
        >
          {col.chars.map((ch, j) => <div key={j}>{ch}</div>)}
        </motion.div>
      ))}
    </div>
  );
}

// ─── Background: Floating Particles (light) ───
function FloatingParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 15 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: i < 2 ? 3 : 1 + Math.random() * 1.5,
      duration: 10 + Math.random() * 8,
      delay: Math.random() * 4,
      drift: (Math.random() - 0.5) * 14,
      isRed: i < 2,
    })), []);

  return (
    <div className="absolute inset-0 pointer-events-none">
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full ${p.isRed ? 'bg-red-500/20' : 'bg-green-500/12'}`}
          style={{
            width: p.size, height: p.size,
            left: `${p.x}%`, top: `${p.y}%`,
            boxShadow: p.isRed ? '0 0 5px rgba(204,0,0,0.15)' : undefined,
          }}
          animate={{ y: [0, -20, 0], x: [0, p.drift, 0], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

// ─── Scanlines ───
function Scanlines() {
  return (
    <div className="absolute inset-0 pointer-events-none z-40 opacity-[0.025]"
      style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.4) 2px, rgba(0,255,65,0.4) 4px)' }} />
  );
}

// ─── HUD Top Bar ───
function TopBar({ solvedCount }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="absolute top-0 left-0 right-0 z-30 h-7 bg-black/60 border-b border-green-800/15 flex items-center px-4 backdrop-blur-sm"
    >
      <span className="font-terminal text-xs tracking-[3px] text-green-700/70 uppercase">
        {PUZZLE_1_WORD_SCRAMBLE.checkpoint}
      </span>
      <div className="ml-auto flex items-center gap-3">
        <span className="font-terminal text-xs text-green-600/70">
          {solvedCount}/{WORDS.length} {PUZZLE_1_WORD_SCRAMBLE.puzzle.decoded.toUpperCase()}
        </span>
        <div className="flex gap-1">
          {WORDS.map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < solvedCount ? 'bg-green-500/60' : 'bg-green-800/30'}`} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Tutorial Screen ───
function DataRecoveryTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-md mx-auto"
    >
      <div className="bg-black/70 border border-green-800/40 rounded-lg p-6 backdrop-blur-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-green-900/40 border border-green-600/30 flex items-center justify-center">
            <Terminal size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-green-400 text-sm tracking-wider">{PUZZLE_1_WORD_SCRAMBLE.tutorial.heading}</h3>
            <p className="text-xs font-terminal text-green-700 tracking-widest mt-0.5">{PUZZLE_1_WORD_SCRAMBLE.tutorial.subheading}</p>
          </div>
        </div>

        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">{PUZZLE_1_WORD_SCRAMBLE.tutorial.procedure}</p>
          <div className="space-y-2.5">
            {PUZZLE_1_WORD_SCRAMBLE.tutorial.steps.map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <span className="text-xs font-orbitron text-green-500 mt-0.5">{step.n}</span>
                <p className="text-xs text-gray-300 font-rajdhani">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">{PUZZLE_1_WORD_SCRAMBLE.tutorial.example}</p>
          <div className="bg-black/40 border border-green-900/20 rounded-lg p-4">
            <p className="text-xs font-terminal text-yellow-600/70 tracking-wider mb-2">HINT: "{PUZZLE_1_WORD_SCRAMBLE.tutorial.exampleHint}"</p>
            <div className="flex justify-center gap-2 mb-3">
              {PUZZLE_1_WORD_SCRAMBLE.tutorial.exampleWord.map((ch, i) => (
                <div key={i} className="w-9 h-10 rounded-md border-2 border-green-600/40 bg-green-950/15 flex items-center justify-center">
                  <span className="font-orbitron text-green-300 text-sm">{ch}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <Check size={10} className="text-green-500" />
              <span className="text-xs font-terminal text-green-500/70 tracking-wider">{PUZZLE_1_WORD_SCRAMBLE.tutorial.exampleStatus}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-green-950/20 border border-green-900/30 rounded px-3 py-2 mb-5">
          <RotateCcw size={12} className="text-green-400 shrink-0" />
          <span className="text-xs font-terminal text-green-400/80 tracking-wider">{PUZZLE_1_WORD_SCRAMBLE.tutorial.warning}</span>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStart}
          className="w-full py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
        >
          <span className="relative z-10">{PUZZLE_1_WORD_SCRAMBLE.tutorial.startButton}</span>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

// ─── HUD Corners ───
function HUDCorners() {
  return <>
    {['top-1 left-1 border-t border-l', 'top-1 right-1 border-t border-r', 'bottom-1 left-1 border-b border-l', 'bottom-1 right-1 border-b border-r'].map((cls, i) => (
      <div key={i} className={`absolute ${cls} w-5 h-5 border-green-600/12 pointer-events-none z-30`} />
    ))}
  </>;
}

// ─── Decoded Words List ───
function DecodedList({ solved }) {
  if (solved.length === 0) return null;
  return (
    <div className="absolute top-10 right-4 space-y-1.5 z-20">
      <p className="text-xs font-terminal tracking-[3px] text-green-800/60 uppercase">{PUZZLE_1_WORD_SCRAMBLE.puzzle.decoded}</p>
      {solved.map((word, i) => (
        <motion.div
          key={word}
          initial={{ opacity: 0, x: 10, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, delay: i * 0.05 }}
          className="flex items-center gap-1.5"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 + i * 0.05 }}
          >
            <Check size={10} className="text-green-500" style={{ filter: 'drop-shadow(0 0 3px rgba(0,255,65,0.4))' }} />
          </motion.div>
          <span className="text-xs font-terminal tracking-[2px] text-green-500/80">{word}</span>
        </motion.div>
      ))}
    </div>
  );
}

// ─── Status Flash Text ───
function StatusFlash({ text, color, visible }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="absolute -top-8 left-0 right-0 z-30 text-center"
        >
          <span className={`font-terminal text-xs tracking-[4px] ${color} uppercase`}
            style={{ textShadow: color.includes('green') ? '0 0 8px rgba(0,255,65,0.4)' : '0 0 8px rgba(255,0,0,0.4)' }}>
            {text}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Puzzle1WordScramble({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [puzzleActive, setPuzzleActive] = useState(false);
  const [currentWord, setCurrentWord] = useState(0);
  const [selected, setSelected] = useState([]);
  const [solved, setSolved] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [flashCorrect, setFlashCorrect] = useState(false);
  const [flashWrong, setFlashWrong] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [statusText, setStatusText] = useState(null);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);
  const transitionRef = useRef(false);

  // Mount-reset pattern
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setPuzzleActive(false);
    setCurrentWord(0);
    setSelected([]);
    setSolved([]);
    setShowSuccess(false);
    setShowFinalLeon(false);
    setTransitioning(false);
    setFlashCorrect(false);
    setFlashWrong(false);
    setScreenShake(false);
    setStatusText(null);
    setSuccessPhase(0);
    setShowProceed(false);
    transitionRef.current = false;
  }, []);

  const scrambledLetters = useMemo(() => {
    return WORDS.map(w => shuffle(w.split('').map((ch, i) => ({ ch, id: i }))));
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setPuzzleActive(true);
  }, []);

  const handleLetterClick = useCallback((letter) => {
    if (!puzzleActive || transitionRef.current) return;
    const newSelected = [...selected, letter];
    setSelected(newSelected);
    const word = WORDS[currentWord];
    if (!word) return;
    const attempt = newSelected.map(l => l.ch).join('');

    if (attempt.length === word.length) {
      if (attempt === word) {
        const newSolved = [...solved, word];
        transitionRef.current = true;
        setTransitioning(true);
        setFlashCorrect(true);
        setScreenShake(true);
        setStatusText({ text: 'Decoded', color: 'text-green-400' });
        setTimeout(() => { setFlashCorrect(false); setScreenShake(false); }, 350);
        setTimeout(() => setStatusText(null), 800);

        if (newSolved.length === WORDS.length) {
          setSolved(newSolved);
          setTimeout(() => {
            setShowSuccess(true);
            setPuzzleActive(false);
            setSuccessPhase(1);
            setTimeout(() => setSuccessPhase(2), 600);
            setTimeout(() => setSuccessPhase(3), 1200);
          }, 500);
          transitionRef.current = false;
          setTimeout(() => setShowFinalLeon(true), 2200);
        } else {
          setTimeout(() => {
            setSolved(newSolved);
            setCurrentWord(newSolved.length);
            setSelected([]);
            setTransitioning(false);
            transitionRef.current = false;
          }, 650);
        }
      } else {
        setFlashWrong(true);
        setScreenShake(true);
        setStatusText({ text: 'Decryption Failed', color: 'text-red-400' });
        setTimeout(() => { setScreenShake(false); }, 300);
        setTimeout(() => { setSelected([]); setFlashWrong(false); setStatusText(null); }, 600);
      }
    }
  }, [puzzleActive, currentWord, solved, selected]);

  const handleRemoveLetter = useCallback((index) => {
    if (transitionRef.current) return;
    setSelected(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.LAB_KEYCARD);
    setShowProceed(true);
  }, [onItemCollected]);

  const word = currentWord < WORDS.length ? WORDS[currentWord] : null;
  const letters = scrambledLetters[currentWord] || [];
  const selectedIds = selected.map(l => l.id);
  const isCorrect = word && selected.length === word.length && selected.map(l => l.ch).join('') === word;

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-word-scramble">
      {/* Background layers */}
      <div className="fixed inset-0 bg-gradient-to-b from-black via-[#020805] to-black" />
      <div className="absolute inset-0 opacity-[0.02] rounded-lg"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,255,65,1) 0px, transparent 1px, transparent 50px), repeating-linear-gradient(90deg, rgba(0,255,65,1) 0px, transparent 1px, transparent 50px)' }} />

      <DataRain />
      <FloatingParticles />
      <Scanlines />
      <HUDCorners />

      {/* Red vignette */}
      <div className="absolute inset-0 pointer-events-none animate-pulse rounded-lg"
        style={{ animationDuration: '6s', boxShadow: 'inset 0 0 80px rgba(100,0,0,0.08)' }} />

      {/* Flash overlays */}
      <AnimatePresence>
        {flashCorrect && (
          <motion.div
            initial={{ opacity: 0.25 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-green-500/6 pointer-events-none z-30 rounded-lg"
          />
        )}
        {flashWrong && (
          <motion.div
            initial={{ opacity: 0.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-red-500/8 pointer-events-none z-30 rounded-lg"
          />
        )}
      </AnimatePresence>

      {/* Screen shake wrapper */}
      <motion.div
        className="relative z-10"
        animate={screenShake ? { x: [0, -3, 3, -2, 2, 0], y: [0, 1, -1, 1, 0] } : {}}
        transition={{ duration: 0.25 }}
      >
        <TopBar solvedCount={solved.length} />
        <DecodedList solved={solved} />

        {/* Header */}
        <div className="text-center mb-6 pt-10 relative z-10">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-green-700/30" />
            <Terminal size={12} className="text-green-600/70" />
            <p className="text-xs font-terminal tracking-[6px] text-green-600/80 uppercase text-glow-green">Checkpoint 01</p>
            <Terminal size={12} className="text-green-600/70" />
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-green-700/30" />
          </div>
          <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">{PUZZLE_1_WORD_SCRAMBLE.title}</h2>
          <p className="text-sm text-green-700/70 font-terminal mt-1">{PUZZLE_1_WORD_SCRAMBLE.subtitle}</p>
        </div>

        {/* Tutorial */}
        <AnimatePresence>
          {showTutorial && <DataRecoveryTutorial onStart={handleTutorialStart} />}
        </AnimatePresence>

        {/* Progress — file tabs */}
        <div className="flex justify-center gap-2 mb-8 relative z-10">
          {WORDS.map((w, i) => {
            const isSolved = i < solved.length;
            const isActive = i === currentWord && puzzleActive;
            return (
              <motion.div
                key={w}
                layout
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-terminal tracking-wider transition-all relative overflow-hidden
                  ${isSolved
                    ? 'bg-green-950/25 border-green-600/40 text-green-400/80'
                    : isActive
                      ? 'border-yellow-600/40 text-yellow-500/90 bg-yellow-950/10'
                      : 'border-gray-800/50 text-gray-600/60'}`}
              >
                {isSolved ? (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}>
                    <Unlock size={10} />
                  </motion.div>
                ) : <Lock size={10} />}
                <span>{WORD_FILES[i]}</span>
                {/* Active scanning line */}
                {isActive && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-px"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(234,179,8,0.4), transparent)' }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Puzzle area */}
        {puzzleActive && !showSuccess && word && (
          <motion.div
            key={currentWord}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-5 relative z-10"
          >
            <div className="max-w-lg mx-auto">
              <div className="relative bg-black/40 border border-green-800/15 rounded-lg p-5 overflow-hidden"
                style={{ boxShadow: '0 0 30px rgba(0,0,0,0.3), inset 0 0 30px rgba(0,0,0,0.2)' }}>
                {/* Corner decorations */}
                {['top-2 left-2 border-t border-l', 'top-2 right-2 border-t border-r', 'bottom-2 left-2 border-b border-l', 'bottom-2 right-2 border-b border-r'].map((cls, i) => (
                  <div key={i} className={`absolute ${cls} w-3 h-3 border-green-800/20`} />
                ))}

                {/* Card border glow */}
                <motion.div
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  animate={{
                    boxShadow: isCorrect
                      ? ['inset 0 0 0 1px rgba(0,255,65,0.1)', 'inset 0 0 0 1px rgba(0,255,65,0.3)', 'inset 0 0 0 1px rgba(0,255,65,0.1)']
                      : ['inset 0 0 0 1px rgba(0,255,65,0.03)', 'inset 0 0 0 1px rgba(0,255,65,0.08)', 'inset 0 0 0 1px rgba(0,255,65,0.03)'],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                <div className="flex items-center gap-2 mb-3">
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <FileCode size={14} className="text-yellow-600/80" />
                  </motion.div>
                  <span className="text-xs font-terminal tracking-[3px] text-yellow-600/70 uppercase">
                    {PUZZLE_1_WORD_SCRAMBLE.puzzle.fileLabel} {currentWord + 1}/{WORDS.length}
                  </span>
                  <div className="flex-1 h-px bg-yellow-900/15 ml-2" />
                </div>

                {/* Hint */}
                <div className="bg-black/30 rounded px-4 py-2.5 border-l-2 border-green-700/20 mb-5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ShieldAlert size={9} className="text-green-800/50" />
                    <p className="text-xs font-terminal text-green-800/60 tracking-wider">{PUZZLE_1_WORD_SCRAMBLE.puzzle.signalIntercept}</p>
                  </div>
                  <p className="text-sm font-special text-gray-300/90 italic">"{WORD_HINTS[currentWord]}"</p>
                </div>

                {/* Answer slots */}
                <div className="flex justify-center gap-2 mb-5 relative" data-testid="answer-slots">
                  <StatusFlash
                    text={statusText?.text}
                    color={statusText?.color}
                    visible={!!statusText}
                  />
                  {Array.from({ length: word.length }).map((_, i) => {
                    const hasLetter = !!selected[i];
                    const isFirstEmpty = !hasLetter && i === selected.length;
                    return (
                      <motion.div
                        key={i}
                        animate={
                          isCorrect && hasLetter
                            ? { scale: [1, 1.1, 1], borderColor: '#22c55e' }
                            : flashWrong && hasLetter
                              ? { x: [-4, 4, -3, 3, 0] }
                              : {}
                        }
                        transition={{ duration: 0.3, delay: isCorrect ? i * 0.05 : 0 }}
                        onClick={() => hasLetter && !transitionRef.current && handleRemoveLetter(i)}
                        className={`w-11 h-12 rounded-md flex items-center justify-center font-orbitron text-lg transition-all border-2 relative overflow-hidden
                          ${hasLetter
                            ? isCorrect
                              ? 'border-green-500 bg-green-950/30 text-green-300 cursor-pointer'
                              : flashWrong
                                ? 'border-red-500 bg-red-950/20 text-red-300'
                                : 'border-green-600/40 bg-green-950/15 text-green-300 cursor-pointer hover:border-green-500/60'
                            : 'border-gray-700/30 bg-black/20 text-transparent'}`}
                        data-testid={`answer-slot-${i}`}
                      >
                        {/* Terminal cursor in first empty slot */}
                        {isFirstEmpty && (
                          <motion.div
                            className="w-0.5 h-5 bg-green-500/50 rounded-full"
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          />
                        )}
                        {!hasLetter && !isFirstEmpty && (
                          <div className="w-1 h-5 bg-green-800/10 rounded-full" />
                        )}
                        {/* Letter with entrance animation */}
                        {hasLetter && (
                          <motion.span
                            initial={{ y: -8, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                          >
                            {selected[i].ch}
                          </motion.span>
                        )}
                        {hasLetter && isCorrect && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.12 }}
                            transition={{ delay: i * 0.05 }}
                            className="absolute inset-0 bg-green-400"
                          />
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-green-900/10" />
                  <ChevronRight size={10} className="text-green-800/20" />
                  <span className="text-xs font-terminal text-green-800/30 tracking-wider flex items-center gap-1.5">
                    {PUZZLE_1_WORD_SCRAMBLE.puzzle.selectLetters}
                    <motion.span
                      animate={{ opacity: [0.2, 0.6, 0.2] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      · · ·
                    </motion.span>
                  </span>
                  <ChevronRight size={10} className="text-green-800/20" />
                  <div className="flex-1 h-px bg-green-900/10" />
                </div>

                {/* Scrambled letters */}
                <div className="flex justify-center gap-2 flex-wrap" data-testid="scrambled-letters">
                  {letters.map((letter) => {
                    const isUsed = selectedIds.includes(letter.id);
                    return (
                      <motion.button
                        key={letter.id}
                        whileHover={!isUsed ? { scale: 1.1, y: -3, boxShadow: '0 0 16px rgba(0,255,65,0.15)' } : {}}
                        whileTap={!isUsed ? { scale: 0.9 } : {}}
                        onClick={() => !isUsed && handleLetterClick(letter)}
                        disabled={isUsed}
                        className={`w-11 h-12 rounded-md font-orbitron text-lg transition-all border relative overflow-hidden
                          ${isUsed
                            ? 'border-gray-800/15 bg-transparent text-gray-800/15 cursor-default'
                            : 'border-green-700/25 bg-green-950/10 text-green-300 cursor-pointer hover:border-green-500/50 hover:bg-green-900/15'}`}
                        data-testid={`letter-${letter.id}`}
                      >
                        <motion.span
                          animate={isUsed ? { opacity: 0.15 } : { opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          {letter.ch}
                        </motion.span>
                        {!isUsed && (
                          <div className="absolute inset-0 bg-gradient-to-b from-green-500/4 to-transparent pointer-events-none" />
                        )}
                        {/* Dim crossbar on used tiles */}
                        {isUsed && (
                          <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            className="absolute top-1/2 left-1 right-1 h-px bg-gray-700/20"
                          />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Reset */}
                <div className="text-center mt-4">
                  <motion.button
                    whileHover={{ scale: 1.05, color: 'rgba(156,163,175,0.8)' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => { if (!transitionRef.current) setSelected([]); }}
                    className="text-xs text-gray-600/60 hover:text-gray-400 font-terminal tracking-wider transition-colors flex items-center gap-1.5 mx-auto group"
                    data-testid="reset-word-btn"
                  >
                    <motion.span
                      className="inline-block"
                      whileTap={{ rotate: -360 }}
                      transition={{ duration: 0.4 }}
                    >
                      <RotateCcw size={10} />
                    </motion.span>
                    {PUZZLE_1_WORD_SCRAMBLE.puzzle.resetButton}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Success screen */}
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center space-y-4 relative z-10 py-12"
          >
            {/* Shockwave ring */}
            {successPhase >= 1 && (
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-green-500/20 rounded-full pointer-events-none"
                initial={{ width: 0, height: 0, opacity: 0.5 }}
                animate={{ width: 300, height: 300, opacity: 0 }}
                transition={{ duration: 1 }}
              />
            )}

            {/* Key icon */}
            <div className="relative w-20 h-20 mx-auto">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 border-2 border-dashed border-green-500/15 rounded-full"
              />
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              >
                <div className="w-16 h-16 rounded-full bg-green-900/25 border-2 border-green-500/70 flex items-center justify-center"
                  style={{ boxShadow: '0 0 25px rgba(0,255,65,0.15), inset 0 0 10px rgba(0,255,65,0.05)' }}>
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Key size={28} className="text-green-400" />
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Text reveals */}
            {successPhase >= 2 && (
              <motion.p
                initial={{ opacity: 0, letterSpacing: '2px' }}
                animate={{ opacity: 1, letterSpacing: '6px' }}
                transition={{ duration: 0.6 }}
                className="font-orbitron text-green-400 text-sm"
                style={{ textShadow: '0 0 10px rgba(0,255,65,0.3)' }}
              >
                {PUZZLE_1_WORD_SCRAMBLE.completion.heading}
              </motion.p>
            )}
            {successPhase >= 3 && (
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="text-gray-400/80 font-rajdhani"
                style={{ textShadow: '0 0 8px rgba(234,179,8,0.15)' }}
              >
                {PUZZLE_1_WORD_SCRAMBLE.completion.itemText}
              </motion.p>
            )}

            {/* Decoded words cascade */}
            {successPhase >= 2 && (
              <div className="flex justify-center gap-3 mt-4">
                {WORDS.map((w, i) => (
                  <motion.span
                    key={w}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 0.5, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.1, duration: 0.3 }}
                    className="text-xs font-terminal tracking-[2px] text-green-600/60"
                  >
                    {w}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {showProceed && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="text-center mt-8 relative z-20"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onComplete?.()}
              className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
            >
              <span className="relative z-10">{PUZZLE_1_WORD_SCRAMBLE.proceedButton}</span>
              <motion.div
                className="absolute inset-0"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
              />
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={PUZZLE_1_WORD_SCRAMBLE.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={PUZZLE_1_WORD_SCRAMBLE.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
